import { supabase } from '@/shared/lib/supabase/client';
import { AppError, unwrapMaybe } from '@/shared/lib/repository/errors';
import type { NotificationPrefsInput, ProfileInput } from '@auditwire/contracts';
import { initialsFromName, mapNotificationPrefs, mapProfile } from '../model/mappers';
import type { NotificationPrefs, Profile } from '../model/types';

const PROFILE_PICTURES_BUCKET = 'profile-pictures';
const PROFILE_PICTURE_EXTENSIONS: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
};

/**
 * Recover the in-bucket object path from a public profile-picture URL, so the
 * previous image can be deleted when one is replaced or removed. Returns null
 * for anything that isn't a URL in this bucket (never guess a path to delete).
 * Exported for unit testing.
 */
export function storagePathFromPublicUrl(publicUrl: string | null): string | null {
  if (!publicUrl) return null;
  const marker = `/storage/v1/object/public/${PROFILE_PICTURES_BUCKET}/`;
  const markerIndex = publicUrl.indexOf(marker);
  if (markerIndex < 0) return null;

  try {
    return decodeURIComponent(publicUrl.slice(markerIndex + marker.length));
  } catch {
    return null;
  }
}

async function updateAvatarUrl(userId: string, avatarUrl: string | null): Promise<Profile> {
  const { data, error } = await supabase
    .from('profiles')
    .update({ avatar_url: avatarUrl })
    .eq('id', userId)
    .select('*')
    .single();
  if (error) throw new AppError(error.message, error.code, error);
  return mapProfile(data);
}

async function removeStoredProfilePicture(publicUrl: string | null): Promise<void> {
  const path = storagePathFromPublicUrl(publicUrl);
  if (!path) return;

  try {
    await supabase.storage.from(PROFILE_PICTURES_BUCKET).remove([path]);
  } catch {
    // Cleanup is best-effort; the profile row must remain consistent if storage is unavailable.
  }
}

/** All profile / notification-preference / account data access. */
export const profileRepository = {
  async get(userId: string): Promise<Profile | null> {
    const row = unwrapMaybe(
      await supabase.from('profiles').select('*').eq('id', userId).maybeSingle(),
    );
    return row ? mapProfile(row) : null;
  },

  async update(userId: string, input: ProfileInput): Promise<Profile> {
    const { data, error } = await supabase
      .from('profiles')
      .update({
        name: input.name,
        initials: initialsFromName(input.name),
        bio: input.bio ?? '',
        avatar_color: input.avatarColor,
        website: input.website ?? '',
        twitter: input.twitter ?? '',
        linkedin: input.linkedin ?? '',
      })
      .eq('id', userId)
      .select('*')
      .single();
    if (error) throw new AppError(error.message, error.code, error);
    return mapProfile(data);
  },

  async uploadProfilePicture(
    userId: string,
    file: File,
    previousUrl: string | null,
  ): Promise<Profile> {
    const extension = PROFILE_PICTURE_EXTENSIONS[file.type];
    if (!extension) throw new AppError('Unsupported profile picture format');

    const path = `${userId}/avatar-${crypto.randomUUID()}.${extension}`;
    const { data, error } = await supabase.storage
      .from(PROFILE_PICTURES_BUCKET)
      .upload(path, file, {
        cacheControl: '31536000',
        contentType: file.type,
        upsert: false,
      });
    if (error) throw new AppError(error.message, error.name, error);

    const {
      data: { publicUrl },
    } = supabase.storage.from(PROFILE_PICTURES_BUCKET).getPublicUrl(data.path);

    let profile: Profile;
    try {
      profile = await updateAvatarUrl(userId, publicUrl);
    } catch (err) {
      await removeStoredProfilePicture(publicUrl);
      throw err;
    }

    await removeStoredProfilePicture(previousUrl);
    return profile;
  },

  async removeProfilePicture(userId: string, currentUrl: string | null): Promise<Profile> {
    const profile = await updateAvatarUrl(userId, null);
    await removeStoredProfilePicture(currentUrl);
    return profile;
  },

  async getNotificationPrefs(userId: string): Promise<NotificationPrefs | null> {
    const row = unwrapMaybe(
      await supabase.from('notification_prefs').select('*').eq('user_id', userId).maybeSingle(),
    );
    return row ? mapNotificationPrefs(row) : null;
  },

  async updateNotificationPrefs(
    userId: string,
    input: NotificationPrefsInput,
  ): Promise<NotificationPrefs> {
    const { data, error } = await supabase
      .from('notification_prefs')
      .upsert({
        user_id: userId,
        reply: input.reply,
        newsletter: input.newsletter,
        new_article: input.newArticle,
        weekly_digest: input.weeklyDigest,
      })
      .select('*')
      .single();
    if (error) throw new AppError(error.message, error.code, error);
    return mapNotificationPrefs(data);
  },

  /** Update auth email and/or password for the current session's user. */
  async updateAccount(changes: { email?: string; password?: string }): Promise<void> {
    if (!changes.email && !changes.password) return;
    const { error } = await supabase.auth.updateUser(changes);
    if (error) throw new AppError(error.message, String(error.status ?? ''), error);
  },
};
